import { ReactElement } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { EmployeeType } from '@prisma/client';
import { Breadcrumb, Button, Form, Layout, Radio, Result, Select, Space } from 'antd';
import { DefaultOptionType } from 'antd/es/select';

import { CreateAgsRoleToDmsProcessMapDto } from '@app/domain-maps-interfaces';
import { Hooks, PageLayout } from '@app/web-components';

import { EmployeeTypeRenderer } from '../../../../../common';
import { ProcessPicker } from '../../eda-vendors/forms/inputs';
import { useAgsRolePicker } from '../../process-management/libs/use-ags-role-picker';
import { useCreateAgsRoleToProcessMap } from '../libs';

export type RoleProcessMapFormData = {
  access: DefaultOptionType;
  process: DefaultOptionType;
  employeeType: EmployeeType;
};

export const CreatePage = () => {
  const create = useCreateAgsRoleToProcessMap();
  const [form] = Form.useForm<RoleProcessMapFormData>();
  const { getSelectProps } = useAgsRolePicker();
  const layoutContentProps = Hooks.useFormLayout();
  const navigate = useNavigate();

  const onFinish = (data: RoleProcessMapFormData) => {
    const dto: CreateAgsRoleToDmsProcessMapDto = {
      externalId: data.access.value?.toString() ?? '',
      processId: typeof data.process.value === 'string' ? parseInt(data.process.value) : data.process.value ?? -1,
      employeeType: data.employeeType,
    };

    create.mutate(dto);
  };

  const employeeType: EmployeeType = (Form.useWatch('access', form)?.label as ReactElement)?.props.employeeType;

  return (
    <Space direction="vertical" style={{ overflow: 'auto' }}>
      <Breadcrumb
        style={{ margin: '16px 0' }}
        items={[
          { title: <Link to="/">Home</Link> },
          { title: 'Admin' },
          { title: <Link to="../">Role Process Map</Link> },
          { title: 'Create Role Process Map' },
        ]}
      />
      <Layout.Content {...layoutContentProps}>
        <PageLayout.Title title="Create Role Process Map" />
        {create.isSuccess ? (
          <Result
            status="success"
            title="Successfully created a role process map!"
            //subTitle={`Process name: ${create.data.data.name}`}
            extra={[
              <Button type="primary" key="go to role process maps" onClick={() => navigate('../')}>
                Go To Role Process Maps
              </Button>,
              <Button
                key="create another"
                onClick={() => {
                  create.reset();
                  form.resetFields();
                }}
              >
                Create Another
              </Button>,
            ]}
          />
        ) : (
          <Form
            form={form}
            layout="vertical"
            onValuesChange={(changedValues) => {
              if (changedValues.access) {
                const empType = changedValues.access.label.props.employeeType;
                if (!empType || empType === EmployeeType.ANY) {
                  form.setFieldValue('employeeType', undefined);
                } else {
                  form.setFieldValue('employeeType', empType);
                  form.validateFields();
                }
              }
            }}
            onFinish={onFinish}
          >
            <Form.Item label="AGS Role" name="access" required rules={[{ required: true }]}>
              <Select {...getSelectProps()} />
            </Form.Item>
            <Form.Item label="Process" name="process" required rules={[{ required: true }]}>
              <ProcessPicker.Select />
            </Form.Item>
            <Form.Item label="Employee Type" name="employeeType" required rules={[{ required: true }]}>
              <Radio.Group
                disabled={!employeeType || employeeType !== EmployeeType.ANY}
                options={[
                  { label: <EmployeeTypeRenderer employeeType={EmployeeType.INTEL} />, value: EmployeeType.INTEL },
                  {
                    label: <EmployeeTypeRenderer employeeType={EmployeeType.CONTINGENT_WORKER} />,
                    value: EmployeeType.CONTINGENT_WORKER,
                  },
                ]}
              />
            </Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                Save
              </Button>
              <Button onClick={() => form.resetFields()}>Reset</Button>
              <Button onClick={() => navigate('../')}>Cancel</Button>
            </Space>
          </Form>
        )}
      </Layout.Content>
    </Space>
  );
};
